import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ChevronDown, Menu, X, Layers } from "lucide-react";
import { company, products } from "../data/mock";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isActive = (p) => pathname === p;

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/products?q=${encodeURIComponent(query.trim())}`);
      setQuery("");
      setMobileOpen(false);
    }
  };

  const navLink = (to, label) => (
    <Link
      to={to}
      className={`px-3 py-2 text-[15px] font-medium transition-colors ${
        isActive(to) ? "text-brand-red" : "text-brand-navy hover:text-brand-red"
      }`}
      onClick={() => setMobileOpen(false)}
    >
      {label}
    </Link>
  );

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" onClick={() => setMobileOpen(false)}>
          <div className="w-12 h-12 rounded-md flex items-center justify-center bg-gradient-to-br from-[#2c3e7a] to-[#1f2444] shadow-md">
            <Layers className="text-white" size={22} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[18px] md:text-[20px] font-extrabold text-brand-navy tracking-wide">
              FOAM SHEET
            </span>
            <span className="text-[12px] md:text-[13px] font-semibold text-brand-red tracking-[0.2em]">
              SUPPLIERS
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLink("/", "Home Page")}
          {navLink("/company-profile", "Company Profile")}
          <div
            className="relative"
            onMouseEnter={() => setShowProducts(true)}
            onMouseLeave={() => setShowProducts(false)}
          >
            <Link
              to="/products"
              className={`flex items-center gap-1 px-3 py-2 text-[15px] font-medium transition-colors ${
                pathname.startsWith("/products") ? "text-brand-red" : "text-brand-navy hover:text-brand-red"
              }`}
            >
              Our Products <ChevronDown size={14} />
            </Link>
            {showProducts && (
              <div className="absolute left-0 top-full pt-2 w-72 z-50">
                <div className="bg-white shadow-xl rounded-md border border-gray-100 py-2 max-h-[420px] overflow-y-auto">
                  {products.slice(0, 12).map((p) => (
                    <Link
                      key={p.id}
                      to={`/products/${p.id}`}
                      className="block px-4 py-2 text-[13px] text-brand-navy hover:bg-gray-50 hover:text-brand-red transition-colors"
                    >
                      {p.name}
                    </Link>
                  ))}
                  <div className="border-t border-gray-100 mt-1">
                    <Link to="/products" className="block px-4 py-2 text-[13px] font-semibold text-brand-red hover:bg-gray-50">
                      View All Products →
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
          {navLink("/contact-us", "Contact Us")}

          {/* Search */}
          <form onSubmit={handleSearch} className="ml-3 flex items-center bg-gray-50 rounded-full overflow-hidden border border-gray-200 focus-within:border-[#2c3e7a]">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Products..."
              className="px-4 py-2 text-sm bg-transparent outline-none w-44"
            />
            <button type="submit" className="bg-brand-blue text-white px-4 py-2.5 hover:opacity-90 transition-opacity" aria-label="Search">
              <Search size={16} />
            </button>
          </form>
        </nav>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 text-brand-navy"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-3 flex flex-col">
            {navLink("/", "Home Page")}
            {navLink("/company-profile", "Company Profile")}
            {navLink("/products", "Our Products")}
            {navLink("/contact-us", "Contact Us")}
            <form onSubmit={handleSearch} className="mt-3 flex items-center bg-gray-50 rounded-full overflow-hidden border border-gray-200">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Products..."
                className="px-4 py-2 text-sm bg-transparent outline-none flex-1"
              />
              <button type="submit" className="bg-brand-blue text-white px-4 py-2.5" aria-label="Search">
                <Search size={16} />
              </button>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}
